import CloseIcon from "@mui/icons-material/Close";
import { Divider, Typography } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as constants from "../../constants";
import ContainedButton from "../../shared/Button/CustomContainedButton";
import OutlinedButton from "../../shared/Button/customOutlinedButton";
import CustomTextField from "../../shared/CustomTextFeild";
import { RootState } from "../../store/store";
import { postCloneInvoice } from "../fiscalUpgrade/services/Invoices/Project/ProjectInvoiceService";
import { getInvoiceNumberByVendorName } from "../fiscalUpgrade/services/Invoices/Vendor/VendorListService";
import { notifyUser } from "../fiscalUpgrade/Utils/notifyUser";

interface propsHeader {
  open: boolean;
  handleClose: () => void;
  invoiceCloneDetails: any;
}

export default function InvoiceClonePopUp(props: propsHeader) {
  const { open, handleClose, invoiceCloneDetails } = props;
  const navigate = useNavigate();
  const User = useSelector((state: RootState) => state.auth);
  const { t } = useTranslation();
  const translate = t as (key: string) => string;
  const [invoiceNumberValue, setInvoiceNumberValue] = useState("");
  const [invoiceNumberError, setInvoiceNumberError] = useState('');
  const [invoiceNames, setInvoiceNames] = useState([]);
  const [blackdropFlag, setBlackdropFlag] = useState(false);

  useEffect(() => {
    if (invoiceCloneDetails?.vendorName && invoiceCloneDetails?.clientId) {
      getInvNumberByVenName(invoiceCloneDetails.vendorName, invoiceCloneDetails.clientId);
    }
  }, [invoiceCloneDetails, open]);

  async function getInvNumberByVenName(vendorNameSearch: string, clientId: number) {
    try {
      const resVendorName = await getInvoiceNumberByVendorName(vendorNameSearch, clientId);
      if (resVendorName.status === constants.STATUS_CODES.SUCCESS) {
        setInvoiceNames(resVendorName.data.data)
      }
    } catch (error: any) {
      if (error instanceof AxiosError) {
        console.error("Axios Error:", error.response?.data || error.message);
      } else {
        console.error("Unexpected Error:", error);
      }
    }
  }

  const handleInvoiceNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInvoiceNumberValue(value);

    if (!value) {
      setInvoiceNumberError(translate("invoiceNumberIsRequired!"));
    } else {
      const lowerTrimmedValue = value.toLowerCase();

      const isDuplicate = invoiceNames.some(
        (item: any) => item.invoiceNumber?.toLowerCase() === lowerTrimmedValue
      );
      if (isDuplicate) {
        setInvoiceNumberError('Invoice with this number already exists');
      } else {
        setInvoiceNumberError('');
      }
    }
  };
  const handleInvoiceNumberBlur = () => {
    const trimmedValue = invoiceNumberValue.trim();
    setInvoiceNumberValue(trimmedValue); // Save trimmed value

    if (!trimmedValue) {
      setInvoiceNumberError(translate("invoiceNumberIsRequired!"));
      return;
    }

    const lowerTrimmedValue = trimmedValue.toLowerCase();

    const isDuplicate = invoiceNames.some(
      (item: any) => item.invoiceNumber?.toLowerCase() === lowerTrimmedValue
    );
    if (isDuplicate) {
      setInvoiceNumberError("Invoice with this number already exists");
    } else {
      setInvoiceNumberError("");
    }
  };

  const handleYesClick = async () => {
    const trimmedValue = invoiceNumberValue.trim();
    if (!trimmedValue) {
      setInvoiceNumberError(translate("invoiceNumberIsRequired!"));
      return;
    }
    if (invoiceNumberError) return;
    postInvClone(invoiceCloneDetails.invoiceId, invoiceNumberValue)
  };




  async function postInvClone(invoiceId: number, newInvoiceNumber: string) {
    setBlackdropFlag(true);
    try {
      const resClonedInv = await postCloneInvoice(invoiceId, newInvoiceNumber);
      if (resClonedInv.status === constants.STATUS_CODES.SUCCESS) {
        notifyUser(resClonedInv?.data?.message, "success", translate('confirmInvoiceClone'));
        setInvoiceNumberValue('');
        setInvoiceNumberError('');
        // Navigate to vendor invoice summary and request a clear of previous view
        navigate("/vendorInvoiceSummary", { state: { shouldClear: true, clearKey: Date.now(), } });
      }
    } catch (error: any) {
      if (error instanceof AxiosError) {
        console.error("Axios Error:", error.response?.data || error.message);
      } else {
        console.error("Unexpected Error:", error);
      }
    } finally {
      handleClose();
      setBlackdropFlag(false);
    }
  }
  const handleNo = () => {
    handleClose();
    setInvoiceNumberValue('');
    setInvoiceNumberError('');
  }
  return (
    <Dialog open={open} sx={{ "& .MuiPaper-root": { borderRadius: "12px", background: "white" } }}>
      <DialogTitle
        sx={{
          backgroundColor: "#059ca9",
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          padding: '10px 10px'
        }}
      >
        {translate("confirmInvoiceClone")}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ color: "white", padding: '0px', ml: 6 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent style={{ paddingTop: '20px' }}>
        <DialogContentText
          sx={{
            whiteSpace: 'normal', // allow wrapping
            overflowWrap: 'break-word', // break long words
            wordBreak: 'break-word', // ensure long words wrap
            fontSize: '1rem'
          }}>

          <Typography sx={{ mb: 2 }}>
            Do you want to Clone the Invoice?
          </Typography>
          <CustomTextField
            labelText={translate("invoiceNumber")}
            required
            value={invoiceNumberValue}
            onChange={handleInvoiceNumberChange}
            error={!!invoiceNumberError}
            inputProps={{ maxLength: 35 }}
            helperText={invoiceNumberError}
            onBlur={handleInvoiceNumberBlur}
          />
          <Typography sx={{ mt: 2 }}>
            <strong style={{ color: 'red' }}>Note: </strong>A maximum of 35 characters are allowed
          </Typography>

        </DialogContentText>
      </DialogContent>
      <Divider />
      <DialogActions>
        <ContainedButton variant="contained" onClick={handleYesClick} disabled={!!invoiceNumberError}>
          {translate('yes')}
        </ContainedButton>
        <OutlinedButton variant="outlined" onClick={handleNo}>{translate('no')}</OutlinedButton>
      </DialogActions>
    </Dialog>
  );
}
